import { normalizeMotivations } from "@/lib/motivationOptions";

const COUNTRY_REASON_PROFILES = {
  PT: {
    fit: [
      { tags: ["ease", "community", "remote", "mobility"], text: "Portugal can feel like one of the softer landings into Europe because it combines EU access with a relocation path many expats and remote workers already understand." },
      { tags: ["budget", "value"], text: "It often gives a better balance of European lifestyle and manageable day-to-day costs than many Western European alternatives." },
      { tags: ["climate", "lifestyle"], text: "Its milder Atlantic-Mediterranean climate suits people who want warmth without committing to a fully tropical environment." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "If you picture Portugal as a bargain, your housing choices will matter more here, especially in Lisbon and the Algarve." },
      { tags: ["language", "integration"], text: "English can help early on, but longer-term integration and bureaucracy become easier with at least some Portuguese." },
      { tags: ["ease", "mobility"], text: "The move can be practical, but tax and residency sequencing still reward careful planning rather than a casual last-minute transition." }
    ]
  },
  ES: {
    fit: [
      { tags: ["healthcare", "stability"], text: "Spain stands out for pairing a warm-weather lifestyle with one of the stronger public-healthcare reputations in this set." },
      { tags: ["climate", "lifestyle", "culture"], text: "It gives you more ways to match the kind of Mediterranean life you want, whether that means a major city, a slower coastal base, or something in between." },
      { tags: ["mobility", "culture"], text: "For people drawn to Europe, Spain can feel like a strong all-around base rather than a niche lifestyle pick." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Spain is strong overall, but it is not the cheapest route into warm-weather living, especially in the most in-demand cities and coastal zones." },
      { tags: ["language", "integration"], text: "The daily experience improves significantly with Spanish, so it may feel less frictionless if you want to rely on English for too long." },
      { tags: ["ease"], text: "Residency can be workable, but it is usually not as effortless as the easiest-transition countries on this page." }
    ]
  },
  CR: {
    fit: [
      { tags: ["nature", "lifestyle", "climate"], text: "Costa Rica makes the most sense when outdoor lifestyle, greenery, and a slower day-to-day rhythm are part of what you are actually optimizing for." },
      { tags: ["stability", "retiree"], text: "It tends to appeal to people who want a calmer, retiree-friendly environment with a long-established international resident presence." },
      { tags: ["climate", "community"], text: "For a warm-climate move that still feels familiar to many North American relocators, Costa Rica often reads as a lifestyle-first choice." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "The idea of Costa Rica as a cheap tropical move can be misleading, especially once you look at imported goods, housing, and popular expat areas." },
      { tags: ["urban", "infrastructure"], text: "If you want highly efficient infrastructure or polished big-city convenience, parts of Costa Rica may feel slower and less streamlined." },
      { tags: ["language", "integration"], text: "Spanish is not always essential immediately, but the transition usually feels smoother if you are willing to operate beyond the expat bubble." }
    ]
  },
  MX: {
    fit: [
      { tags: ["budget", "value"], text: "Mexico is one of the clearest value plays in this set if you want your budget to stretch without giving up major-city or expat-hub options." },
      { tags: ["community", "integration", "remote"], text: "It offers multiple relocation styles at once — urban, colonial, beach, or highland — which makes it easier to fit around your actual lifestyle preferences." },
      { tags: ["ease", "timezone"], text: "For North American users in particular, Mexico can feel more practical than farther-flung options because travel time, time zones, and familiarity are easier to manage." }
    ],
    tradeoffs: [
      { tags: ["safety", "stability"], text: "Mexico can work very well, but city choice matters more here than in some higher-consistency countries, especially if safety and predictability are priorities." },
      { tags: ["healthcare"], text: "Healthcare can be very solid in the right locations, though the quality gap between regions is wider than in stronger healthcare-system leaders." },
      { tags: ["language", "integration"], text: "If you want the move to feel genuinely easy beyond expat enclaves, Spanish becomes much more important over time." }
    ]
  },
  TH: {
    fit: [
      { tags: ["budget", "value"], text: "Thailand is one of the strongest budget-maximizing options here if your goal is to keep everyday costs meaningfully lower while maintaining lifestyle flexibility." },
      { tags: ["remote", "community", "urban"], text: "It has one of the more established remote-work and expat ecosystems in Asia, especially if you want a choice between big-city energy and lower-cost lifestyle hubs." },
      { tags: ["climate", "lifestyle"], text: "For someone who truly wants full-time heat and tropical living, Thailand is a much more decisive climate match than milder Mediterranean options." }
    ],
    tradeoffs: [
      { tags: ["language", "integration"], text: "The day-to-day language barrier is more real here than in many European or English-friendlier options, especially once you move beyond the most internationally oriented neighborhoods." },
      { tags: ["timezone"], text: "If you expect to stay tightly connected to North America or Europe, the distance and time-zone gap can become a practical strain." },
      { tags: ["ease", "remote"], text: "Long-term residency can work, but the path is more structured than the old 'just show up and stay' perception some people still have." }
    ]
  },
  PA: {
    fit: [
      { tags: ["ease", "value", "stability"], text: "Panama tends to appeal to people who want a practical relocation base with stronger banking, infrastructure, and day-to-day administrative familiarity than many regional peers." },
      { tags: ["budget", "value"], text: "For users earning or budgeting in dollars, the USD-based economy removes one layer of financial uncertainty from the move." },
      { tags: ["retiree", "ease"], text: "It often makes the most sense for retirees, business-minded movers, or anyone prioritizing practical setup over romance or cultural immersion." }
    ],
    tradeoffs: [
      { tags: ["climate"], text: "Panama can feel more hot, humid, and urban-commercial than people expect if they are imagining a purely lifestyle-first tropical move." },
      { tags: ["budget", "value"], text: "Its value proposition is more about convenience and structure than ultra-low costs, so it may disappoint if your first filter is pure affordability." },
      { tags: ["lifestyle"], text: "Outside the strongest hubs, the overall lifestyle offering can feel narrower than in countries with more distinct regional personalities." }
    ]
  },
  EC: {
    fit: [
      { tags: ["budget", "value"], text: "Ecuador can be attractive when you want lower costs without fully giving up access to a dollarized economy and a range of climate zones." },
      { tags: ["nature", "climate"], text: "It gives you more flexibility than many people expect, especially if you want to choose between mountain cities, coast, and a slower lifestyle pace." },
      { tags: ["retiree", "lifestyle"], text: "For users open to a quieter rhythm and fewer big-city expectations, Ecuador can feel more livable than its headline visibility suggests." }
    ],
    tradeoffs: [
      { tags: ["infrastructure", "healthcare"], text: "It may ask for more compromise on infrastructure consistency and healthcare confidence than the stronger-fit leaders on this page." },
      { tags: ["language", "integration"], text: "The move usually works better if you are prepared for more Spanish dependence in daily life than in heavily internationalized hubs." },
      { tags: ["ease"], text: "It can make sense as a value option, but the overall transition often feels less polished and less predictable than the most established relocation pathways." }
    ]
  },
  MY: {
    fit: [
      { tags: ["language", "integration", "healthcare"], text: "Malaysia stands out for giving you stronger English usability and good private-healthcare value in a part of the world where that combination is not automatic." },
      { tags: ["urban", "value"], text: "It can be a strong fit if you want modern infrastructure and city convenience without paying Singapore-level prices." },
      { tags: ["remote", "lifestyle"], text: "For users open to Southeast Asia but wanting a slightly easier daily transition than Thailand or Vietnam, Malaysia often feels more balanced." }
    ],
    tradeoffs: [
      { tags: ["climate"], text: "The climate is persistently hot and humid, so it works best if you genuinely want tropical living rather than occasional warmth." },
      { tags: ["ease"], text: "Long-term stay policies can shift, so some of the appeal depends on comfort with evolving rules rather than a fixed, simple path." },
      { tags: ["lifestyle"], text: "If your ideal move is highly walkable, highly historic, or culturally European in feel, Malaysia may not scratch that same itch." }
    ]
  },
  CO: {
    fit: [
      { tags: ["climate", "urban", "culture"], text: "Colombia can work well if you want a more energetic urban relocation with strong city identity and the ability to choose climate by elevation rather than by country." },
      { tags: ["value", "budget"], text: "It often gives better value than many lifestyle-forward city options, especially if you want a real urban base rather than a resort-style move." },
      { tags: ["remote", "community"], text: "For some remote or flexible workers, cities like Medellín feel attractive because they combine livability, community, and lower costs in one package." }
    ],
    tradeoffs: [
      { tags: ["safety", "stability"], text: "Colombia asks you to be more intentional about city and neighborhood choice if safety and overall predictability are central to your decision." },
      { tags: ["language", "integration"], text: "Spanish matters quickly here if you want the move to feel integrated rather than temporary or insulated." },
      { tags: ["infrastructure"], text: "The quality of daily infrastructure can vary more from one place to another than in countries that feel stronger as all-around system plays." }
    ]
  },
  VN: {
    fit: [
      { tags: ["budget", "value"], text: "Vietnam is compelling when your first priority is stretching budget without giving up vibrant city life, food culture, or day-to-day energy." },
      { tags: ["remote", "urban", "culture"], text: "It suits people who want a faster-moving, more locally immersive Southeast Asia experience rather than a polished expat bubble." },
      { tags: ["climate", "lifestyle"], text: "For warm-weather users who are open to something dynamic and urban, Vietnam can feel more stimulating than calmer alternatives." }
    ],
    tradeoffs: [
      { tags: ["language", "integration"], text: "Language friction is meaningful here, so the move can feel more demanding if you want a low-friction daily setup." },
      { tags: ["urban", "infrastructure"], text: "Traffic, density, and air-quality tradeoffs can matter more in Vietnam than in calmer or more systematized relocation options." },
      { tags: ["timezone"], text: "Like other Asia-based choices, it can feel logistically far away if your life remains heavily anchored to Western time zones." }
    ]
  },
  FR: {
    fit: [
      { tags: ["healthcare", "stability"], text: "France becomes more compelling when healthcare quality, public systems, and institutional depth are higher priorities than pure affordability." },
      { tags: ["culture", "mobility"], text: "It offers a richer 'live in Europe' experience for users who care about cultural depth, rail connectivity, and regional access rather than just warm weather." },
      { tags: ["urban", "lifestyle"], text: "For someone who wants polished cities and a high-service environment, France can feel more substantial than lower-cost lifestyle alternatives." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "France usually requires more financial margin than stronger-value countries on this page, particularly if you expect major-city access." },
      { tags: ["language", "integration"], text: "French matters more here than in the more English-tolerant European options, especially if you want smoother long-term integration." },
      { tags: ["ease"], text: "The country rewards thorough documentation and patience, so it is less appealing if you want the most straightforward possible relocation path." }
    ]
  },
  DE: {
    fit: [
      { tags: ["stability", "infrastructure", "healthcare"], text: "Germany is strongest when you value reliability, infrastructure, and system depth more than climate or lifestyle romance." },
      { tags: ["urban", "career", "mobility"], text: "It can make sense for users who want a serious European base with strong city networks and professional credibility." },
      { tags: ["healthcare", "stability"], text: "As a pure practicality play, Germany often reads as more robust than the sunnier but less system-heavy alternatives." }
    ],
    tradeoffs: [
      { tags: ["climate"], text: "If warmth is central to your vision of living abroad, Germany may feel misaligned even if it performs well on systems and stability." },
      { tags: ["ease"], text: "The move can be orderly, but your housing choices will matter more here, and bureaucracy still makes it less effortless than its efficient reputation suggests." },
      { tags: ["language", "integration"], text: "English can cover a lot professionally, yet full day-to-day integration usually becomes easier once German enters the picture." }
    ]
  },
  CA: {
    fit: [
      { tags: ["language", "stability", "healthcare"], text: "Canada is attractive when familiarity, strong institutions, and an easier English-speaking transition matter more than dramatic lifestyle change." },
      { tags: ["safety", "stability"], text: "It tends to feel like a lower-friction move for users who want predictability, healthcare confidence, and social stability." },
      { tags: ["urban", "nature"], text: "The fit is usually about quality of life and livability rather than cost savings, especially if you want access to major cities plus nature." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Canada is hard to justify as a value move if your budget discipline is one of the biggest drivers of the decision." },
      { tags: ["climate"], text: "For people prioritizing warmth, much of Canada can feel like a direct mismatch even if the overall systems fit is strong." },
      { tags: ["lifestyle"], text: "It may feel less transformative than other options here if your goal is a more distinct change in pace, climate, or daily culture." }
    ]
  },
  NL: {
    fit: [
      { tags: ["language", "integration", "urban"], text: "The Netherlands is compelling if you want one of the easier daily transitions into continental Europe without losing urban sophistication." },
      { tags: ["infrastructure", "mobility"], text: "It performs well for users who value efficient transit, walkable cities, and a highly connected European base." },
      { tags: ["career", "community"], text: "For internationally oriented professionals, it can feel more immediately navigable than many neighboring countries." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Housing pressure is a real constraint here, so the country can feel financially tighter than its polished image suggests." },
      { tags: ["climate"], text: "If your idea of relocation includes warmth and sunshine, the Dutch climate may remain a weak point no matter how strong the infrastructure is." },
      { tags: ["space", "lifestyle"], text: "It is a strong systems choice, but not always the best match if you want spacious living or a slower, more relaxed rhythm." }
    ]
  },
  NZ: {
    fit: [
      { tags: ["nature", "safety", "stability"], text: "New Zealand often makes the most sense for people who care deeply about safety, scenery, and a calmer quality-of-life profile." },
      { tags: ["language", "integration"], text: "The English-speaking environment lowers the everyday friction of the move in a way many otherwise-attractive countries cannot match." },
      { tags: ["lifestyle", "nature"], text: "It feels more like a deliberate lifestyle choice than a purely economic one, especially if outdoor living matters." }
    ],
    tradeoffs: [
      { tags: ["timezone"], text: "New Zealand is logistically far from almost everything, which can matter more than expected if your personal or professional life remains internationally anchored." },
      { tags: ["budget", "value"], text: "It is rarely the budget winner, so the case for New Zealand usually depends on lifestyle priorities rather than financial efficiency." },
      { tags: ["career", "urban"], text: "If you want very large-city energy or the broadest professional market, it may feel smaller and more limited than the major global hubs." }
    ]
  },
  AU: {
    fit: [
      { tags: ["language", "healthcare", "lifestyle"], text: "Australia can be a strong fit when you want English-speaking ease, strong healthcare confidence, and a genuinely outdoor-oriented urban lifestyle." },
      { tags: ["stability", "remote", "urban"], text: "It works well for people who want a polished, high-functioning relocation rather than a low-cost or experimental one." },
      { tags: ["climate", "lifestyle"], text: "For users chasing warmth, coast, and city infrastructure all at once, Australia can feel more complete than many alternatives." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Australia generally asks for real financial capacity, so it is a harder case to make if cost control is central." },
      { tags: ["timezone"], text: "Distance and time-zone separation can become a meaningful lifestyle cost if you expect to stay closely connected elsewhere." },
      { tags: ["ease"], text: "Residency and immigration pathways are not usually the easiest part of the Australia story, especially compared with softer-entry countries." }
    ]
  },
  JP: {
    fit: [
      { tags: ["healthcare", "safety", "stability"], text: "Japan becomes compelling when healthcare quality, safety, reliability, and public-order confidence matter more than ease or spontaneity." },
      { tags: ["urban", "culture"], text: "It offers one of the most distinct high-functioning urban lifestyles in the set, especially for users drawn to structure, design, and dense city living." },
      { tags: ["stability", "infrastructure"], text: "As an all-around systems country, Japan can feel especially strong when reliability, infrastructure, and institutional depth matter more than pure lifestyle ease." }
    ],
    tradeoffs: [
      { tags: ["language", "integration"], text: "Language and day-to-day integration are usually the biggest friction points here, especially if you want life outside a narrow expat routine." },
      { tags: ["ease"], text: "It is rarely the simplest transition path from a residency standpoint, so the move often requires more intentional setup." },
      { tags: ["budget", "lifestyle"], text: "Japan can justify its strengths, but it usually asks for more compromise on budget and living space than the warmer value-focused options." }
    ]
  },
  SG: {
    fit: [
      { tags: ["language", "integration", "stability"], text: "Singapore is one of the easiest Asia-based transitions if you care about English usability, safety, and polished day-to-day infrastructure." },
      { tags: ["urban", "career", "remote"], text: "It is strongest for users who want a highly efficient city-state base rather than a sprawling lifestyle destination." },
      { tags: ["stability", "healthcare"], text: "From a systems perspective, Singapore can feel unusually high-confidence across healthcare, transit, and general daily execution." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Singapore is difficult to frame as a value play because the convenience and efficiency come with very real cost pressure." },
      { tags: ["lifestyle", "space"], text: "If your ideal move involves space, variety, or a looser rhythm, Singapore can feel tightly controlled and physically compact." },
      { tags: ["ease"], text: "It is excellent operationally, but the long-term right to stay is not automatically as simple as the daily experience suggests." }
    ]
  },
  IT: {
    fit: [
      { tags: ["culture", "climate", "lifestyle"], text: "Italy makes the most sense when your decision is partly about quality of daily life, food, place, and a warmer European rhythm rather than pure efficiency." },
      { tags: ["mobility", "culture"], text: "It gives you a strong cultural version of European access, especially if you value living somewhere with real regional personality rather than generic convenience." },
      { tags: ["climate", "lifestyle"], text: "For users wanting Mediterranean warmth with more emotional pull than a systems-first country, Italy can feel more aligned." }
    ],
    tradeoffs: [
      { tags: ["ease"], text: "Italy can be rewarding, but it is rarely the most straightforward administrative move, especially if you prefer predictability and process clarity." },
      { tags: ["infrastructure", "healthcare"], text: "The experience varies materially by region, so the fit depends more on where in Italy you land than in some more consistent countries." },
      { tags: ["language", "integration"], text: "If you want to move beyond surface-level enjoyment into practical ease, Italian becomes much more important over time." }
    ]
  },
  GR: {
    fit: [
      { tags: ["climate", "lifestyle"], text: "Greece can feel attractive if warm Mediterranean living is a real priority and you want that lifestyle at a lower cost than some Western European peers." },
      { tags: ["value", "mobility"], text: "It offers a more value-oriented path into Europe for users who care about climate and pace more than maximum institutional polish." },
      { tags: ["lifestyle", "retiree"], text: "For people drawn to a slower rhythm and coastal living, Greece can feel more lifestyle-led than purely strategic in a good way." }
    ],
    tradeoffs: [
      { tags: ["healthcare", "infrastructure"], text: "Compared with the top healthcare leaders, Greece may ask for more compromise on system depth and consistency." },
      { tags: ["ease"], text: "The move can work well, but administrative pace is not always as smooth as the destination’s lifestyle appeal suggests." },
      { tags: ["language", "integration"], text: "English may carry the early transition, though day-to-day integration still gets easier if you are open to navigating some Greek." }
    ]
  },
  HR: {
    fit: [
      { tags: ["climate", "nature", "lifestyle"], text: "Croatia can appeal if you want Adriatic coastal living, a slower pace, and a smaller-scale European environment rather than a major-system powerhouse." },
      { tags: ["safety", "lifestyle"], text: "It often reads as a calmer lifestyle choice for users who care about scenery, safety, and day-to-day simplicity." },
      { tags: ["mobility", "value"], text: "As a European base, Croatia can feel more intimate and less overwhelming than larger alternatives, which some users prefer." }
    ],
    tradeoffs: [
      { tags: ["career", "urban"], text: "Croatia can feel limiting if you want the deepest professional market, biggest-city energy, or the strongest international ecosystem." },
      { tags: ["healthcare", "infrastructure"], text: "It does not usually compete with the top healthcare-and-infrastructure leaders, so the fit is more about lifestyle than system strength." },
      { tags: ["seasonality"], text: "Coastal living can also mean more seasonality and a narrower year-round rhythm than people expect from a summer-facing image." }
    ]
  },
  UY: {
    fit: [
      { tags: ["stability", "safety"], text: "Uruguay tends to appeal when you want Latin America with a calmer, more institutionally steady, and more understated overall feel." },
      { tags: ["retiree", "lifestyle"], text: "It often works best for users who care about slower pace, personal safety, and a quieter long-term base more than excitement or scale." },
      { tags: ["climate", "stability"], text: "For people who want regional familiarity without the same level of volatility as some neighbors, Uruguay can feel reassuringly even-tempered." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Uruguay is frequently more expensive than people expect relative to the rest of Latin America, which weakens the value story." },
      { tags: ["urban", "community"], text: "If you want a very large expat or big-city ecosystem, Uruguay can feel smaller and quieter than stronger-fit alternatives." },
      { tags: ["lifestyle"], text: "Its strengths are subtle rather than dramatic, so it may feel less compelling if you want a move with obvious upside across multiple dimensions." }
    ]
  },
  AR: {
    fit: [
      { tags: ["culture", "urban", "value"], text: "Argentina can be attractive if you want a culturally rich, city-oriented lifestyle that feels more substantial than a pure budget destination." },
      { tags: ["climate", "lifestyle"], text: "It offers meaningful climate and lifestyle variety, which can matter if you do not want your whole relocation decision anchored to one narrow environment type." },
      { tags: ["culture", "urban"], text: "For users drawn to urban depth and European-style city life in Latin America, Argentina can feel uniquely distinctive." }
    ],
    tradeoffs: [
      { tags: ["stability", "budget"], text: "Economic volatility is the main caveat here, because the value story can shift and create planning uncertainty over time." },
      { tags: ["ease"], text: "That volatility means the move often requires more adaptability and comfort with change than calmer alternatives on this page." },
      { tags: ["stability"], text: "Even if daily life appeals, it may feel less stable as a long-horizon planning choice than the stronger all-around fits above it." }
    ]
  },
  CL: {
    fit: [
      { tags: ["stability", "infrastructure"], text: "Chile becomes more compelling when you want Latin America with stronger infrastructure, more institutional order, and a more structured overall feel." },
      { tags: ["nature", "lifestyle"], text: "It can suit users who want serious access to outdoors and landscape variety without giving up an urban base entirely." },
      { tags: ["safety", "stability"], text: "Compared with some regional alternatives, Chile often reads as a more stability-oriented choice than a purely lifestyle-first one." }
    ],
    tradeoffs: [
      { tags: ["budget", "value"], text: "Chile is not the easiest Latin American option to justify on cost alone, especially once you compare it with lower-cost peers." },
      { tags: ["climate"], text: "It is also a less obvious fit if your move is centered on warm-weather ease rather than structure and system confidence." },
      { tags: ["distance"], text: "The country’s geography can make internal distance and day-to-day logistics feel less simple than the map first suggests." }
    ]
  }
};

const buildProfileTags = (summary = {}) => {
  const tags = new Set();
  const priorities = summary.priorities || [];
  const motivations = normalizeMotivations(summary.motivations || []);
  const lifestylePreferences = summary.lifestyle_preferences || [];

  if (summary.budget || priorities.includes("cost_of_living")) {
    tags.add("budget");
    tags.add("value");
  }

  if (priorities.includes("healthcare") || ["very_important", "important"].includes(summary.healthcare_importance)) {
    tags.add("healthcare");
  }

  if (priorities.includes("safety")) {
    tags.add("safety");
    tags.add("stability");
  }

  if (priorities.includes("community")) {
    tags.add("community");
    tags.add("integration");
  }

  if (priorities.includes("culture")) {
    tags.add("culture");
  }

  if (priorities.includes("nature")) {
    tags.add("nature");
  }

  if (priorities.includes("career")) {
    tags.add("career");
    tags.add("urban");
  }

  if (summary.remote_work) {
    tags.add("remote");
  }

  if (summary.work_abroad_intent === "remote_self_employed") {
    tags.add("remote");
    tags.add("ease");
  }

  if (summary.work_abroad_intent === "local_employment") {
    tags.add("career");
    tags.add("urban");
  }

  if (summary.work_abroad_intent === "no") {
    tags.add("retiree");
    tags.add("ease");
  }

  if (summary.timeline === "asap") {
    tags.add("ease");
  }

  if (summary.intended_length_of_stay === "long_term_relocation") {
    tags.add("stability");
  }

  if (summary.intended_length_of_stay === "trial_exploratory_move") {
    tags.add("ease");
    tags.add("lifestyle");
  }

  if (summary.language_flexibility === "english_only") {
    tags.add("language");
    tags.add("integration");
  }

  if (summary.need_reliable_internet) {
    tags.add("internet");
    tags.add("infrastructure");
  }

  if (["warm", "temperate", "tropical"].includes(summary.climate)) {
    tags.add("climate");
  }

  if (["family_young", "family_older"].includes(summary.family_status)) {
    tags.add("family");
    tags.add("stability");
  }

  if (summary.family_status === "couple") {
    tags.add("lifestyle");
  }

  if (motivations.includes("safety") || motivations.includes("political_climate")) {
    tags.add("safety");
    tags.add("stability");
  }

  if (motivations.includes("lower_cost_of_living")) {
    tags.add("budget");
    tags.add("value");
  }

  if (motivations.includes("better_healthcare")) {
    tags.add("healthcare");
  }

  if (motivations.includes("slower_pace_less_stress")) {
    tags.add("lifestyle");
    tags.add("ease");
  }

  if (motivations.includes("work_or_business_opportunities")) {
    tags.add("career");
    tags.add("urban");
  }

  if (motivations.includes("retirement_planning")) {
    tags.add("retiree");
    tags.add("ease");
  }

  if (motivations.includes("education_or_family_reasons")) {
    tags.add("family");
    tags.add("stability");
  }

  if (motivations.includes("closer_to_family")) {
    tags.add("family");
    tags.add("ease");
    tags.add("lifestyle");
  }

  if (motivations.includes("adventure_or_new_experience")) {
    tags.add("culture");
    tags.add("nature");
    tags.add("lifestyle");
  }

  if (lifestylePreferences.includes("arts_culture") || lifestylePreferences.includes("fine_dining")) {
    tags.add("culture");
    tags.add("urban");
  }

  if (lifestylePreferences.includes("nightlife_entertainment")) {
    tags.add("urban");
    tags.add("culture");
  }

  if (lifestylePreferences.includes("public_transportation") || lifestylePreferences.includes("walkability")) {
    tags.add("mobility");
    tags.add("infrastructure");
    tags.add("urban");
  }

  if (lifestylePreferences.includes("beach_environment")) {
    tags.add("climate");
    tags.add("lifestyle");
  }

  if (lifestylePreferences.includes("outdoor_activities")) {
    tags.add("nature");
    tags.add("lifestyle");
  }

  if (summary.infrastructure_expectation === "world_class") {
    tags.add("infrastructure");
    tags.add("healthcare");
  }

  if (summary.infrastructure_expectation === "modern") {
    tags.add("infrastructure");
  }

  return tags;
};

export const getRankedCountryReasonCandidates = (country, summary, reasonType = "fit") => {
  const profile = COUNTRY_REASON_PROFILES[country.code] || { fit: [], tradeoffs: [] };
  const candidates = profile[reasonType] || [];
  const profileTags = buildProfileTags(summary);

  return candidates
    .map((candidate, index) => {
      const matchCount = candidate.tags.filter((tag) => profileTags.has(tag)).length;
      return {
        ...candidate,
        score: matchCount * 10 - index,
      };
    })
    .sort((a, b) => b.score - a.score);
};

export const getCountryReasonCandidates = (country, summary, reasonType = "fit") => Array.from(
  new Map(getRankedCountryReasonCandidates(country, summary, reasonType).map((candidate) => [candidate.text, candidate])).values()
).map((candidate) => candidate.text);